import { useState } from 'react';
import { Alert, Button, Divider, Group, Loader, Paper, Stack, Table, Text, Textarea, TextInput, Title, Tooltip } from '@mantine/core';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { StoresIssueResponse, StoresIssueUpdate } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { formatSastDate } from '../../../lib/sastDate';
import { ConfirmButton } from '../../../components/ConfirmButton';
import { DocStatusBadge } from '../../../components/DocStatusBadge';
import { CostCentreName, LocationName, ProductName, SiteName } from '../../../features/stores-transfers/components/EntityNames';
import { CostCentreSelect } from '../../../features/stores-transfers/components/CostCentreSelect';
import { SiteSelect } from '../../../features/stores-transfers/components/SiteSelect';
import { LedgerPreview } from '../../../features/stores-transfers/components/LedgerPreview';
import { LineRowsEditor, type LineFormRow } from '../../../features/stores-transfers/components/LineRowsEditor';
import { formatDate, formatQty } from '../../../features/stores-transfers/format';
import {
  useCreateStoresIssue,
  usePostStoresIssue,
  useReverseStoresIssue,
  useStoresIssue,
  useUpdateStoresIssue,
} from '../../../features/stores-transfers/api';

/** Owned by G24 (plan.md S0). Stores-issue detail: editable while DRAFT (edit lines
 * + Post, with the same ledger-effect preview the create flow shows), read-only once
 * POSTED with a "Reverse & clone to draft" action once REVERSED per §4.1. */
export const Route = createFileRoute('/_authenticated/stores-transfers/issues/$issueId')({
  component: IssueDetailPage,
});

function IssueDetailPage() {
  const { issueId } = Route.useParams();
  const { data: issue, isLoading, isError } = useStoresIssue(issueId);

  if (isLoading) {
    return <Loader />;
  }
  if (isError || !issue) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />}>
        Stores issue not found.
      </Alert>
    );
  }

  return issue.status === 'DRAFT' ? <DraftIssueEditor key={issue.id} issue={issue} /> : <PostedIssueView issue={issue} />;
}

function toLineFormRows(issue: StoresIssueResponse): LineFormRow[] {
  return issue.lines.map((line) => ({
    key: line.id,
    productId: line.productId,
    productLabel: '',
    locationId: line.fromLocationId,
    locationLabel: '',
    qty: line.qty,
  }));
}

function DraftIssueEditor({ issue }: { issue: StoresIssueResponse }) {
  const canOperate = useCanOperate();
  const updateIssue = useUpdateStoresIssue(issue.id);
  const postIssue = usePostStoresIssue(issue.id);

  const [siteId, setSiteId] = useState(issue.siteId);
  const [costCentreId, setCostCentreId] = useState(issue.costCentreId);
  // SAST calendar date, not `.toISOString().slice(0, 10)` UTC truncation (spec §4
  // item 7, Amendment 2026-07-10): an issue saved in the first two SAST hours of a
  // day would otherwise prefill the edit form with the previous day's date.
  const [issuedDate, setIssuedDate] = useState(() => formatSastDate(issue.issuedAt));
  const [notes, setNotes] = useState(issue.notes ?? '');
  const [lines, setLines] = useState<LineFormRow[]>(() => toLineFormRows(issue));
  const [error, setError] = useState<string | null>(null);

  const linesValid = lines.length > 0 && lines.every((l) => l.productId && l.locationId && l.qty && Number(l.qty) > 0);

  function buildPayload(): StoresIssueUpdate {
    return {
      siteId,
      costCentreId,
      issuedAt: new Date(issuedDate),
      notes: notes.trim() || null,
      lines: lines.map((l) => ({ productId: l.productId, fromLocationId: l.locationId, qty: l.qty })),
    };
  }

  async function handleSave() {
    setError(null);
    try {
      await updateIssue.mutateAsync(buildPayload());
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to save changes.');
    }
  }

  async function handlePost() {
    setError(null);
    try {
      await updateIssue.mutateAsync(buildPayload());
      await postIssue.mutateAsync(undefined);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to post the issue.');
    }
  }

  return (
    <Stack gap="lg" maw={860}>
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={2}>{issue.number}</Title>
          <DocStatusBadge status={issue.status} />
        </Group>
        <Button component={Link} to="/stores-transfers" variant="subtle">
          Back to register
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="md">
          <SiteSelect
            label="Site"
            value={siteId}
            onChange={(id) => {
              setSiteId(id);
              setCostCentreId('');
            }}
            required
          />
          <CostCentreSelect
            label="Cost centre"
            siteId={siteId || undefined}
            value={costCentreId}
            onChange={(id) => setCostCentreId(id)}
            required
          />
          <TextInput type="date" label="Issued date" value={issuedDate} onChange={(event) => setIssuedDate(event.currentTarget.value)} required />
          <Textarea label="Notes" value={notes} onChange={(event) => setNotes(event.currentTarget.value)} autosize minRows={2} />
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <LineRowsEditor rows={lines} onChange={setLines} siteId={siteId || undefined} includeLocation locationLabel="From location" />
      </Paper>

      <Paper withBorder radius="md" p="md">
        <LedgerPreview siteId={siteId || undefined} lines={lines} attribution={<>Cost centre: <CostCentreName id={costCentreId} /></>} />
      </Paper>

      <Group justify="flex-end">
        <Tooltip label="Requires Operations role" disabled={canOperate}>
          <span>
            <Button variant="default" disabled={!canOperate} onClick={handleSave} loading={updateIssue.isPending && !postIssue.isPending}>
              Save changes
            </Button>
          </span>
        </Tooltip>
        <Tooltip label="Requires Operations role" disabled={canOperate}>
          <span>
            <Button onClick={handlePost} loading={postIssue.isPending} disabled={!canOperate || !linesValid}>
              Post
            </Button>
          </span>
        </Tooltip>
      </Group>
    </Stack>
  );
}

function PostedIssueView({ issue }: { issue: StoresIssueResponse }) {
  const canWrite = useCanOperate();
  const navigate = useNavigate();
  const reverseIssue = useReverseStoresIssue(issue.id);
  const createIssue = useCreateStoresIssue();
  const [error, setError] = useState<string | null>(null);
  const [reversing, setReversing] = useState(false);

  async function handleReverseAndClone() {
    setError(null);
    setReversing(true);
    try {
      await reverseIssue.mutateAsync(undefined);
      // "Clone" half of "reverse & clone to draft" (BUILD_SPEC.md §4.1): the reversal
      // only ever changes ledger movements + this document's own status, so the
      // just-reversed issue's own header/lines are exactly what to prefill — build a
      // brand-new DRAFT from them and land the user straight on it.
      const clone = await createIssue.mutateAsync({
        siteId: issue.siteId,
        costCentreId: issue.costCentreId,
        issuedAt: new Date(),
        notes: issue.notes,
        lines: issue.lines.map((line) => ({ productId: line.productId, fromLocationId: line.fromLocationId, qty: line.qty })),
      });
      void navigate({ to: '/stores-transfers/issues/$issueId', params: { issueId: clone.id } });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to reverse this issue.');
    } finally {
      setReversing(false);
    }
  }

  return (
    <Stack gap="lg" maw={860}>
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={2}>{issue.number}</Title>
          <DocStatusBadge status={issue.status} />
        </Group>
        <Button component={Link} to="/stores-transfers" variant="subtle">
          Back to register
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="xs">
          <Group gap={6}>
            <Text fw={600}>Site:</Text>
            <SiteName id={issue.siteId} />
          </Group>
          <Group gap={6}>
            <Text fw={600}>Cost centre:</Text>
            <CostCentreName id={issue.costCentreId} />
          </Group>
          <Group gap={6}>
            <Text fw={600}>Issued:</Text>
            <Text>{formatDate(issue.issuedAt)}</Text>
          </Group>
          {issue.notes && (
            <Group gap={6} align="flex-start">
              <Text fw={600}>Notes:</Text>
              <Text>{issue.notes}</Text>
            </Group>
          )}
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Product</Table.Th>
              <Table.Th>From location</Table.Th>
              <Table.Th>Qty</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {issue.lines.map((line) => (
              <Table.Tr key={line.id}>
                <Table.Td>
                  <ProductName id={line.productId} />
                </Table.Td>
                <Table.Td>
                  <LocationName id={line.fromLocationId} />
                </Table.Td>
                <Table.Td>{formatQty(line.qty)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {issue.status === 'POSTED' && (
        <>
          <Divider />
          <Group justify="flex-end">
            <Tooltip label="Requires Operations role" disabled={canWrite}>
              <span>
                <ConfirmButton
                  color="amber"
                  variant="light"
                  disabled={!canWrite}
                  loading={reversing}
                  label="Reverse & clone to draft"
                  message={`Reverse stores issue ${issue.number}? Its stock will be backed out by a reversing ledger entry, and a new DRAFT issue will be created for you to redo.`}
                  onConfirm={handleReverseAndClone}
                />
              </span>
            </Tooltip>
          </Group>
        </>
      )}
      {issue.status === 'REVERSED' && (
        <Text c="dimmed" size="sm">
          This issue has been reversed. Its ledger effect has been undone; see the register for any draft that superseded
          it.
        </Text>
      )}
    </Stack>
  );
}
